/**
 * CoPilot Service
 *
 * API service for AI-powered guidance and recommendations.
 */

import { DOMAIN_ERROR_CODES } from '@tonnie/foundation';
import { TonnieClient } from '../client/base';
import type { CoPilotTrace, CoPilotPageContext, CoPilotMode } from '@tonnie/copilot';
import { readCoPilotStream } from '@tonnie/copilot';
import type {
  CoPilotStreamEvent,
  CoPilotActionProposal,
  CoPilotAttachmentKind,
  CoPilotSuggestion,
  CoPilotSuggestionCard,
  SafetyBannerConfig,
} from '@tonnie/types';

export type { CoPilotSuggestion, CoPilotSuggestionId } from '@tonnie/types';

/**
 * Streaming-capable fetch. Defaults to global `fetch` (web streams response
 * bodies natively). React Native passes `expo/fetch` — RN's GLOBAL fetch
 * buffers the whole body, so mobile MUST inject `expo/fetch` here (SR2 P0.11/
 * P1.2, decision T6).
 */
export type StreamingFetch = (
  url: string,
  init: {
    method: string;
    headers: Record<string, string>;
    body: string;
    signal?: AbortSignal;
  }
) => Promise<{ ok: boolean; status: number; body: ReadableStream<Uint8Array> | null }>;

/** A file the user attached to a turn — carried so the Companion can act on it. */
export interface CoPilotTurnAttachment {
  storageKey: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  kind: CoPilotAttachmentKind;
}

export interface StreamInteractInput {
  message: string;
  context?: {
    hero?: string;
    screen?: string;
    portal?: 'journey' | 'citizen' | 'business';
    /** User-chosen answer length/depth (Brief / Balanced / Detailed). */
    responseStyle?: 'brief' | 'balanced' | 'detailed';
    /**
     * Page-context: the entity whose detail the user has open (e.g. a talent
     * detail drawer). Lets the Companion resolve "this talent" with no explicit
     * id. The backend still pipeline-gates the resolved id.
     */
    entityId?: string;
    entityType?: string;
    /**
     * The journey on screen — the citizen's current journey, or the journey of
     * the company's open talent drawer (X6/X8). A hint the server verifies.
     */
    journeyInstanceId?: string;
    /**
     * The app's configured locale (BCP-47, e.g. 'pt-BR'). The CoPilot ALWAYS
     * replies in this language regardless of the language the user typed; the
     * server validates it and backstops with the persisted users.locale.
     */
    locale?: string;
    /**
     * Per-turn consent for the web-search tool (composer "Search the web" toggle,
     * BL-213 v2). The Companion may reach the live web on THIS turn only when this
     * is true (and the feature/plan gate allows it); otherwise it never does.
     */
    webSearchConsent?: boolean;
    /**
     * The conversation to continue (0089 history). Omit for a brand-new chat —
     * the server lazily creates the session on the first turn and returns its id
     * on the `end` frame, which the engine adopts as `currentConversationId`.
     */
    conversationId?: string;
  };
  /**
   * True when this turn's message came from an upload (voice note / image /
   * document). Escalates any mutation proposal to a typed confirmation (G§22).
   */
  untrustedContent?: boolean;
  /** A file attached to this turn — enables the file → Vault/task actions. */
  attachment?: CoPilotTurnAttachment;
}

/** One row in the conversation-history list (0089). */
export interface CoPilotConversationSummary {
  id: string;
  title: string;
  hero: string | null;
  /** ISO timestamp of the last message, or null. Sort key (newest first). */
  lastMessageAt: string | null;
  messageCount: number;
}

/** Result of a Companion upload — extracted text + file metadata to attach. */
export interface CoPilotUploadResult {
  attachmentId: string;
  kind: CoPilotAttachmentKind;
  /** transcript (audio) | description (image) | extracted text (document). */
  text: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
}

/** Common file-metadata fields every upload endpoint returns. */
interface UploadResponse {
  attachmentId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
}
function uploadMeta(r: UploadResponse): UploadResponse {
  return {
    attachmentId: r.attachmentId,
    fileName: r.fileName,
    mimeType: r.mimeType,
    fileSize: r.fileSize,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface CoPilotMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  context?: {
    hero?: string;
    section?: string;
  };
  /**
   * The safety banner an answer was shown with (D2) — a reopened thread opens
   * with its last answer's. Null for a user turn or an answer with no trace.
   */
  safetyBanner?: SafetyBannerConfig | null;
}

export interface CoPilotFeedbackInput {
  messageId: string;
  helpful: boolean;
  comment?: string;
}

export interface InteractInput {
  message?: string;
  context?: Partial<CoPilotPageContext>;
  mode?: CoPilotMode;
  activeHybridSessionId?: string;
  activeGuideFlowId?: string;
  hybridScenarioId?: string;
  guideFlowId?: string;
  debug?: boolean;
}

export interface InteractResponse {
  trace: CoPilotTrace;
  mode?: CoPilotMode;
  [key: string]: unknown;
}

export interface CoPilotQuota {
  used: number;
  limit: number;
  remaining: number;
  /** SR2 P1.3 — tier + UX fields for the quota indicator / 80% warning. */
  planTier?: string;
  unlimited?: boolean;
  /** ISO timestamp of the next local-midnight reset. */
  resetAt?: string;
  /** True once usage crosses 80% of the limit. */
  warn?: boolean;
}

export interface GuideStateInput {
  currentTaskId?: string;
  currentChatId?: string;
  completedTasks?: string[];
  completedChats?: string[];
  variables?: Record<string, unknown>;
  chatHistory?: Array<{
    chatId: string;
    taskId: string;
    timestamp: string;
    type: string;
    data: unknown;
  }>;
  /**
   * Rendered UI conversation bubbles (`IMessage[]`). Persisted on
   * the server alongside `chatHistory` so the chat panel can be
   * repainted on resume from any device — without depending on
   * localStorage.
   */
  chatMessages?: Array<Record<string, unknown>>;
  status?: string;
  progress?: number;
  journeyInstanceId?: string;
  /**
   * Optimistic-concurrency token. The hook layer fills this from the
   * cached `GuideStateResponse.rowVersion` automatically — callers do
   * not need to thread it manually. A 409 from the server signals
   * another tab/device modified the row first.
   */
  expectedRowVersion?: number;
}

/**
 * Raised by the guide-state mutations when the server returns 409.
 * Carries the server's current rowVersion so the caller (typically
 * the SDK hook layer) can surface "your session was updated
 * elsewhere" UI and refetch the row before retry.
 *
 * Mirrors the backend's `GuideStateConflictError` shape.
 */
export class GuideStateConflictSdkError extends Error {
  constructor(
    public readonly flowId: string,
    public readonly expected: number | undefined,
    public readonly actual: number | undefined,
    public readonly journeyInstanceId: string | null
  ) {
    super(
      `Guide state for "${flowId}" was modified by another session (expected=${expected}, actual=${actual}).`
    );
    this.name = 'GuideStateConflictSdkError';
  }
}

export interface GuideStateResponse {
  id: string;
  flowId: string;
  journeyInstanceId?: string | null;
  currentTaskId: string | null;
  currentChatId: string | null;
  completedTasks: string[];
  completedChats: string[];
  variables: Record<string, unknown>;
  chatHistory: Array<{
    chatId: string;
    taskId: string;
    timestamp: string;
    type: string;
    data: unknown;
  }>;
  /**
   * Rendered UI conversation bubbles. Server returns whatever the
   * frontend last persisted; consumers cast to their own `IMessage[]`
   * shape on read.
   */
  chatMessages?: Array<Record<string, unknown>>;
  status: string;
  progress: number;
  // Lifecycle fields — added 2026-04-25 for FlowLifecycleManager.
  // Server populates these on every GET; older clients tolerantly
  // ignore them (they're optional).
  startedAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  lastActiveAt?: string | null;
  resumeCount?: number;
  restartCount?: number;
  retakeCount?: number;
  /** Per-widget persistent state, keyed "<chatId>::<widgetName>". */
  widgetStates?: Record<string, Record<string, unknown>>;
  /**
   * Optimistic-concurrency token. Reader sees it on GET, writer sends
   * it back as `If-Match` on save / finalize / patchWidgetState; the
   * server rejects with 409 on mismatch.
   */
  rowVersion?: number;
}

/**
 * Inspect a thrown SDK error; if it represents a guide-state version
 * conflict (HTTP 409 with `code === 'GUIDE_STATE_CONFLICT'`), return a
 * typed `GuideStateConflictSdkError` with the rowVersion payload.
 * Otherwise return null. Kept module-level so the service can call
 * it from each mutation without re-importing TonnieApiError checks.
 */
export function toGuideStateConflict(
  err: unknown,
  flowId: string
): GuideStateConflictSdkError | null {
  if (!err || typeof err !== 'object') return null;
  const e = err as {
    status?: number;
    code?: string;
    data?: { expectedRowVersion?: number; actualRowVersion?: number; journeyInstanceId?: string };
  };
  if (e.status !== 409) return null;
  // Registry-backed, not a literal. This guard was correct all along and still
  // never fired: the server emitted `code: 'GuideStateConflictError'` (the class
  // name), so this returned null for every real 409 and `guard()` rethrew the
  // raw error. FlowLifecycleManager's refetch-and-retry therefore NEVER ran —
  // a guide-state write conflict lost the citizen's work behind a generic
  // error. Fixed in HttpExceptionFilter; the constant keeps it that way.
  if (e.code !== DOMAIN_ERROR_CODES.GUIDE_STATE_CONFLICT) return null;
  return new GuideStateConflictSdkError(
    flowId,
    e.data?.expectedRowVersion,
    e.data?.actualRowVersion,
    e.data?.journeyInstanceId ?? null
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════

export class CoPilotService {
  constructor(private client: TonnieClient) {}

  /**
   * Get chat history
   */
  async getChatHistory(params?: {
    limit?: number;
    offset?: number;
  }): Promise<{ messages: CoPilotMessage[]; total: number }> {
    return this.client.get<{ messages: CoPilotMessage[]; total: number }>('/api/copilot/history', {
      params: params as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * Clear the current conversation (deactivates the active session) so the next
   * turn starts fresh with no memory of the prior thread.
   */
  async clearChatHistory(): Promise<{ success: boolean }> {
    return this.client.delete<{ success: boolean }>('/api/copilot/history');
  }

  // ── Conversation history (0089): list · search · continue · rename · delete ──

  /**
   * The user's conversations for the history panel — non-deleted, ≥1 message,
   * newest first. `query` matches title OR message body (ChatGPT-style search).
   */
  async listConversations(params?: {
    query?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ conversations: CoPilotConversationSummary[]; total: number }> {
    return this.client.get<{ conversations: CoPilotConversationSummary[]; total: number }>(
      '/api/copilot/conversations',
      { params: params as Record<string, string | number | boolean | undefined> }
    );
  }

  /** A chosen conversation's messages (select-to-continue). */
  async getConversationMessages(
    id: string,
    params?: { limit?: number; offset?: number }
  ): Promise<{ messages: CoPilotMessage[]; total: number }> {
    return this.client.get<{ messages: CoPilotMessage[]; total: number }>(
      `/api/copilot/conversations/${id}/messages`,
      { params: params as Record<string, string | number | boolean | undefined> }
    );
  }

  /** Rename a conversation. */
  async renameConversation(id: string, title: string): Promise<{ id: string; title: string }> {
    return this.client.patch<{ id: string; title: string }>(`/api/copilot/conversations/${id}`, {
      title,
    });
  }

  /** Soft-delete a conversation (tombstone; never physical). */
  async deleteConversation(id: string): Promise<{ success: boolean }> {
    return this.client.delete<{ success: boolean }>(`/api/copilot/conversations/${id}`);
  }

  /**
   * Get proactive suggestions
   */
  async getSuggestions(): Promise<CoPilotSuggestion[]> {
    return this.client.get<CoPilotSuggestion[]>('/api/copilot/suggestions');
  }

  /**
   * Submit feedback on a CoPilot response
   */
  async submitFeedback(input: CoPilotFeedbackInput): Promise<{ success: boolean }> {
    return this.client.post<{ success: boolean }>('/api/copilot/feedback', input);
  }

  /**
   * Unified interact endpoint — routes to appropriate CoPilot engine.
   * Every response includes a CoPilotTrace.
   */
  async interact(input: InteractInput): Promise<InteractResponse> {
    return this.client.post<InteractResponse>('/api/copilot/interact', input);
  }

  /**
   * Streaming Brain Companion turn (SR2 P1.2, TECH §18/§23). Opens the SSE
   * `interact/stream` endpoint with a fresh auth token and yields typed
   * protocol events via the shared `readCoPilotStream` reader — identical
   * parsing on web (global fetch) and native (`expo/fetch`, injected).
   *
   * The headless `@tonnie/copilot/client` conversation runner consumes this.
   * Attaches Authorization itself (SSE streams bypass the fetch interceptor,
   * same pattern as the talent-activity stream).
   */
  async *streamInteract(
    input: StreamInteractInput,
    opts?: { signal?: AbortSignal; fetchImpl?: StreamingFetch }
  ): AsyncGenerator<CoPilotStreamEvent> {
    const token = await this.client.getFreshAuthToken();
    const url = new URL('/api/copilot/interact/stream', this.client.getBaseUrl()).toString();
    const doFetch = opts?.fetchImpl ?? (globalThis.fetch as unknown as StreamingFetch);

    const response = await doFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(input),
      signal: opts?.signal,
    });

    if (!response.ok || !response.body) {
      throw new Error(`CoPilot stream failed: HTTP ${response.status}`);
    }
    yield* readCoPilotStream(response.body);
  }

  /**
   * Per-capability kill-switch state (SR2 P0.7) — the FE reads this to render
   * degraded UI honestly (voice off / uploads off).
   */
  async getCapabilities(): Promise<Record<string, boolean>> {
    return this.client.get<Record<string, boolean>>('/api/copilot/capabilities');
  }

  // ── Action proposals (SR2 P2.1 confirm loop) ─────────────────────────────

  /** Pending proposals for a session (client rehydration). */
  async listProposals(sessionId?: string): Promise<CoPilotActionProposal[]> {
    return this.client.get<CoPilotActionProposal[]>('/api/copilot/proposals', {
      params: sessionId ? { sessionId } : undefined,
    });
  }

  /**
   * Confirm + execute a proposal — the ONLY path that runs a mutation (TECH
   * §21). `typedToken` is required for guarded (Tier-2 / untrusted) actions.
   * `outcome: 'stale'` means the world changed since the suggestion.
   */
  async confirmProposal(
    proposalId: string,
    typedToken?: string
  ): Promise<{ outcome: 'success' | 'stale'; result?: unknown }> {
    return this.client.post<{ outcome: 'success' | 'stale'; result?: unknown }>(
      `/api/copilot/proposals/${proposalId}/confirm`,
      // Field name MUST match the endpoint's zod schema (`typedConfirmation`).
      // It was `typedToken`, so the token never arrived → the server compared
      // the stored token against undefined → every guarded confirm 400'd.
      typedToken ? { typedConfirmation: typedToken } : {}
    );
  }

  /** Dismiss a proposal without executing. */
  async dismissProposal(proposalId: string): Promise<{ dismissed: boolean }> {
    return this.client.post<{ dismissed: boolean }>(
      `/api/copilot/proposals/${proposalId}/dismiss`,
      {}
    );
  }

  // ── Multimodal uploads (SR2 P3.1–P3.3) ───────────────────────────────────

  /** Upload a voice note → transcript. Send `text` as the next (untrusted) turn. */
  async uploadAudio(file: File | Blob): Promise<CoPilotUploadResult> {
    const form = new FormData();
    form.append('file', file);
    const res = await this.client.postForm<UploadResponse & { transcript: string }>(
      '/api/copilot/uploads/audio',
      form
    );
    return { ...uploadMeta(res), kind: 'audio', text: res.transcript };
  }

  /** Upload an image → vision description + text transcription. */
  async uploadImage(file: File | Blob): Promise<CoPilotUploadResult> {
    const form = new FormData();
    form.append('file', file);
    const res = await this.client.postForm<UploadResponse & { extractedText: string }>(
      '/api/copilot/uploads/image',
      form
    );
    return { ...uploadMeta(res), kind: 'image', text: res.extractedText };
  }

  /** Upload a PDF/DOCX → extracted text. */
  async uploadDocument(file: File | Blob): Promise<CoPilotUploadResult> {
    const form = new FormData();
    form.append('file', file);
    const res = await this.client.postForm<UploadResponse & { extractedText: string }>(
      '/api/copilot/uploads/document',
      form
    );
    return { ...uploadMeta(res), kind: 'document', text: res.extractedText };
  }

  // ── Proactive corner-cue suggestions (SR2 P5.1) ──────────────────────────

  /** The visible corner-cue cards, as stored. A pure read (F52). */
  async getProactive(): Promise<CoPilotSuggestionCard[]> {
    return this.client.get<CoPilotSuggestionCard[]>('/api/copilot/proactive');
  }

  /**
   * Compute the caller's corner cues and return the visible ones
   * (deterministic, no LLM). The server paces it: inside its window this
   * returns the stored cards without computing (F52).
   */
  async evaluateProactive(): Promise<CoPilotSuggestionCard[]> {
    return this.client.post<CoPilotSuggestionCard[]>('/api/copilot/proactive/evaluate', {});
  }

  /** Mark a cue seen (engagement metric). */
  async markProactiveSeen(id: string): Promise<{ ok: boolean }> {
    return this.client.post<{ ok: boolean }>(`/api/copilot/proactive/${id}/seen`, {});
  }

  /** Dismiss a cue — it won't return (dedupe = don't repeat). */
  async dismissProactive(id: string): Promise<{ ok: boolean }> {
    return this.client.post<{ ok: boolean }>(`/api/copilot/proactive/${id}/dismiss`, {});
  }

  /**
   * Get current user's CoPilot quota.
   */
  async getQuota(): Promise<CoPilotQuota> {
    return this.client.get<CoPilotQuota>('/api/copilot/quota');
  }

  /**
   * Get persisted guide state for a flow, optionally scoped to a journey.
   */
  async getGuideState(
    flowId: string,
    journeyInstanceId?: string
  ): Promise<GuideStateResponse | null> {
    const params = journeyInstanceId ? `?journeyInstanceId=${journeyInstanceId}` : '';
    return this.client.get<GuideStateResponse | null>(
      `/api/copilot/guide-state/${flowId}${params}`
    );
  }

  /**
   * Save guide state for a flow.
   *
   * `expectedRowVersion` (when present) is checked server-side. A
   * version mismatch surfaces as `GuideStateConflictSdkError` (HTTP 409).
   */
  async saveGuideState(flowId: string, state: GuideStateInput): Promise<GuideStateResponse> {
    return this.guard(flowId, () =>
      this.client.post<GuideStateResponse>(`/api/copilot/guide-state/${flowId}`, state)
    );
  }

  /**
   * Translate the generic 409 `TonnieApiError` into the typed
   * `GuideStateConflictSdkError` for any guide-state mutation.
   *
   * Centralised here so each mutation method is one line of
   * boilerplate. Non-409 errors propagate untouched.
   */
  private async guard<T>(flowId: string, fn: () => Promise<T>): Promise<T> {
    try {
      return await fn();
    } catch (err) {
      const conflict = toGuideStateConflict(err, flowId);
      if (conflict) throw conflict;
      throw err;
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle endpoints — added 2026-04-25 for the FlowLifecycleManager
  // (resume / leave / restart / retake / discard / widget-state).
  // See apps/platform-api/.../copilot.controller.ts for contracts.
  // ──────────────────────────────────────────────────────────────────

  /** Mark that the user resumed an active flow (idempotent within 5min idle). */
  async markGuideResume(
    flowId: string,
    journeyInstanceId?: string
  ): Promise<{ resumeCount: number; counted: boolean }> {
    const params = journeyInstanceId ? `?journeyInstanceId=${journeyInstanceId}` : '';
    return this.client.post(`/api/copilot/guide-state/${flowId}/resume${params}`, {});
  }

  /** Explicit destructive restart — wipes progress, bumps restart_count. */
  async restartGuide(
    flowId: string,
    body: { reason?: string; expectedRowVersion?: number } = {},
    journeyInstanceId?: string
  ): Promise<{ ok: boolean; id?: string; restartCount?: number; rowVersion?: number }> {
    const params = journeyInstanceId ? `?journeyInstanceId=${journeyInstanceId}` : '';
    return this.guard(flowId, () =>
      this.client.post(`/api/copilot/guide-state/${flowId}/restart${params}`, body)
    );
  }

  /** Retake an assessment — restart + hero-specific archive. Throws on setup flows. */
  async retakeGuide(
    flowId: string,
    body: {
      reason?: string;
      archivePreviousResult?: boolean;
      expectedRowVersion?: number;
    } = {},
    journeyInstanceId?: string
  ): Promise<{
    ok: boolean;
    id?: string;
    retakeCount?: number;
    archived?: boolean;
    rowVersion?: number;
  }> {
    const params = journeyInstanceId ? `?journeyInstanceId=${journeyInstanceId}` : '';
    return this.guard(flowId, () =>
      this.client.post(`/api/copilot/guide-state/${flowId}/retake${params}`, body)
    );
  }

  /** Soft-cancel — marks status='cancelled'. */
  async discardGuide(
    flowId: string,
    body: { reason?: string; expectedRowVersion?: number } = {},
    journeyInstanceId?: string
  ): Promise<{ ok: boolean; id?: string; rowVersion?: number }> {
    const params = journeyInstanceId ? `?journeyInstanceId=${journeyInstanceId}` : '';
    return this.guard(flowId, () =>
      this.client.post(`/api/copilot/guide-state/${flowId}/discard${params}`, body)
    );
  }

  /** Patch a widget's persistent state (debounced from useWidgetState). */
  async patchGuideWidgetState(
    flowId: string,
    body: {
      widgetKey: string;
      partial: Record<string, unknown>;
      expectedRowVersion?: number;
    },
    journeyInstanceId?: string
  ): Promise<{
    ok: boolean;
    widgetStates?: Record<string, Record<string, unknown>>;
    rowVersion?: number;
  }> {
    const params = journeyInstanceId ? `?journeyInstanceId=${journeyInstanceId}` : '';
    return this.guard(flowId, () =>
      this.client.patch(`/api/copilot/guide-state/${flowId}/widget-state${params}`, body)
    );
  }

  /**
   * Atomic finalize: writes final state + widget snapshot + flips
   * status to 'completed' in a single DB transaction. Replaces the
   * fan-out of save + multiple widget PATCHes + status flip the
   * frontend used to do, which could leave rows partially-completed
   * if a network blip landed mid-flush.
   */
  async finalizeGuideState(
    flowId: string,
    body: {
      currentTaskId?: string;
      currentChatId?: string;
      completedTasks?: string[];
      completedChats?: string[];
      variables?: Record<string, unknown>;
      chatHistory?: GuideStateResponse['chatHistory'];
      progress?: number;
      widgetStates?: Record<string, Record<string, unknown>>;
      journeyInstanceId?: string;
      expectedRowVersion?: number;
    }
  ): Promise<GuideStateResponse> {
    return this.guard(flowId, () =>
      this.client.post(`/api/copilot/guide-state/${flowId}/finalize`, body)
    );
  }
}

export function createCoPilotService(client: TonnieClient): CoPilotService {
  return new CoPilotService(client);
}
